// OP-3605 — Regras puras do Razão, espelho de `domain/estoque.ts`
// (`NAO_ESTORNAVEL`, `podeEstornar`) e de `ESTORNAVEIS_PELA_ROTA` do
// `movimentoAvulsoService`. Gates visuais; quem recusa é o servidor.
import { Controle, MovimentoRow, TipoMovimento, UnidadeRow } from '../../../../types/estoque'

export const TIPO_MOVIMENTO_LABEL: Record<TipoMovimento, string> = {
  SALDO_INICIAL: 'Saldo inicial', ENTRADA: 'Entrada', SEPARACAO: 'Separação',
  CANCELAMENTO_SEPARACAO: 'Cancelamento de separação', SAIDA: 'Saída', ENTREGA: 'Entrega',
  COLETA: 'Coleta', RECEBIMENTO: 'Recebimento', CONFERENCIA: 'Conferência',
  SAIDA_TRANSFERENCIA: 'Saída por transferência', ENTRADA_TRANSFERENCIA: 'Entrada por transferência',
  SAIDA_MANUTENCAO: 'Saída para manutenção', RETORNO_MANUTENCAO: 'Retorno de manutenção',
  CONSUMO: 'Consumo', AJUSTE: 'Ajuste', EXTRAVIO: 'Extravio', BAIXA: 'Baixa',
}

/**
 * Movimentos que o estorno não desfaz — espelho de `NAO_ESTORNAVEL`.
 * BAIXA passou pela gerência; SALDO_INICIAL é fato histórico; CONSUMO não tem
 * destino para inverter (material que volta é ENTRADA nova, não estorno).
 */
export const NAO_ESTORNAVEL: TipoMovimento[] = ['BAIXA', 'SALDO_INICIAL', 'CONSUMO']

/**
 * Referências cujo lançamento nasceu solto — as únicas que a rota de estorno
 * aceita (`ESTORNAVEIS_PELA_ROTA`). Movimento de pedido, transferência,
 * inventário, manutenção ou contrato tem documento do outro lado e se corrige
 * pelo fluxo, não por estorno.
 */
export const REFERENCIAS_ESTORNAVEIS = ['AVULSO', 'COMPRA']

/**
 * O primeiro impedimento do estorno, ou null.
 *
 * Os dois campos do vínculo apontam para lados opostos e não se confundem:
 * `movimentoEstornoId` marca que ESTA linha é o inverso de outra;
 * `estornadoPorId` marca que ela já foi desfeita. Trocá-los deixava o original
 * oferecendo "Estornar" depois de estornado e rotulava o inverso como se ele
 * é que tivesse sido desfeito.
 */
export function impedimentoEstorno(m: Pick<MovimentoRow, 'tipo' | 'referenciaTipo' | 'estornadoPorId'> & { unidadeId?: number | null }): string | null {
  if (m.estornadoPorId != null) return 'Este lançamento já foi estornado.'
  if (NAO_ESTORNAVEL.indexOf(m.tipo) >= 0) {
    return `${TIPO_MOVIMENTO_LABEL[m.tipo] ?? m.tipo} não se estorna: a correção é um lançamento novo.`
  }
  const reaparecimento = m.tipo === 'EXTRAVIO' && m.unidadeId != null
  if (!reaparecimento && REFERENCIAS_ESTORNAVEIS.indexOf(String(m.referenciaTipo)) < 0) {
    return 'Lançamento de um fluxo (pedido, transferência, inventário, manutenção ou contrato): corrija pelo próprio fluxo.'
  }
  return null
}

/**
 * Esta linha é o inverso de outra — serve para ROTULAR, não para bloquear.
 *
 * Estornar um estorno o servidor aceita: ele é o último lançamento da peça e
 * nasceu solto, então passa por `podeEstornar` como qualquer outro. Tratar
 * isto como impedimento seria inventar uma regra que o domínio não tem.
 */
export const ehLancamentoDeEstorno = (m: Pick<MovimentoRow, 'movimentoEstornoId'>): boolean =>
  m.movimentoEstornoId != null

/** O servidor exige motivo; a tela não manda o POST sem ele. */
export const motivoEstornoValido = (motivo: string): boolean => motivo.trim().length > 0

/**
 * A peça pode ter andado depois deste lançamento — e aí o estado de antes já
 * não descreve nada (`PECA_MOVEU_DEPOIS`). Só o servidor sabe; a tela avisa
 * quando o lançamento não é o mais recente da peça NA PÁGINA, que é o caso
 * comum e barato de detectar.
 */
export function talvezTenhaMovidoDepois(linhas: MovimentoRow[], m: MovimentoRow): boolean {
  if (m.unidadeId == null) return false
  return linhas.some((o) => o.unidadeId === m.unidadeId && o.id > m.id)
}


// ---- Lançamento avulso (ESL9) ---------------------------------------------

/**
 * Os ÚNICOS dois tipos que nascem soltos — espelho de `TIPOS_AVULSOS`.
 *
 * Todo o resto tem fluxo e alçada próprios: separação e entrega vêm do pedido,
 * transferência da transferência, AJUSTE só da aprovação de inventário, BAIXA
 * só da gerência, ENTRADA do cadastro. Aceitar outro tipo aqui seria porta dos
 * fundos para o razão.
 */
export const TIPOS_AVULSOS = ['CONSUMO', 'EXTRAVIO'] as const
export type TipoAvulso = typeof TIPOS_AVULSOS[number]

export const TIPO_AVULSO_UI: Record<TipoAvulso, { rotulo: string; explica: string; regime: Controle }> = {
  CONSUMO: {
    rotulo: 'Consumo de material',
    explica: 'Material que foi usado e não volta. Sai do saldo da praça, sem destino: é saída do sistema.',
    regime: 'QUANTIDADE',
  },
  EXTRAVIO: {
    rotulo: 'Extravio de peça',
    explica: 'A peça sumiu. Ela não sai do lugar no razão — passa a constar como extraviada, e o caminho seguinte é a fila de baixa.',
    regime: 'SERIAL',
  },
}

export interface FormAvulso {
  tipo: TipoAvulso | ''
  itemId: number | null
  localId: number | null
  unidadeId: number | null
  quantidade: string
  motivo: string
  referenciaId: string
}

/**
 * O primeiro impedimento do lançamento avulso, ou null.
 *
 * `CONSUMO` exige referência porque consumo sem lastro é saldo sumindo sem
 * explicação (`CONSUMO_EXIGE_REFERENCIA`); `EXTRAVIO` exige a peça. A
 * referência tem teto de 40 caracteres e o servidor recusa em vez de cortar.
 */
export function impedimentoAvulso(f: FormAvulso): string | null {
  if (!f.tipo) return 'Escolha o tipo de lançamento.'
  if (!f.itemId) return 'Escolha o item.'
  if (!f.localId) return 'Escolha a praça.'
  if (!f.motivo.trim()) return 'Explique o motivo: ele fica no razão para sempre.'
  if (f.referenciaId.trim().length > 40) return 'A referência passa de 40 caracteres.'
  if (f.tipo === 'CONSUMO') {
    const q = f.quantidade.trim()
    if (!q) return 'Informe a quantidade consumida.'
    if (!/^\d+$/.test(q) || Number(q) <= 0) return 'A quantidade precisa ser um inteiro maior que zero.'
    if (!f.referenciaId.trim()) return 'Consumo precisa de referência: o documento ou evento que o explica.'
  }
  if (f.tipo === 'EXTRAVIO' && !f.unidadeId) return 'Escolha a peça extraviada.'
  return null
}


/**
 * As peças que estão NESTA praça agora — não as que apenas têm base nela.
 *
 * `getEstoqueUnidadesDoItem` devolve as duas coisas (`local_id = praça OR
 * local_base_id = praça`), porque a tela de equipamentos quer ver o que saiu.
 * Um lançamento avulso, porém, declara a praça como ORIGEM, e origem tem de
 * ser onde a peça está: oferecer a que está com o cliente é convidar o
 * usuário a um `UNIDADE_EM_OUTRO_LOCAL`.
 */
export function pecasDoLocal(pecas: UnidadeRow[], localId: number | null): UnidadeRow[] {
  if (localId == null) return []
  return pecas.filter((u) => Number(u.localId) === Number(localId))
}
